import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, ChevronRight, MessageCircle } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import { trackButtonClick } from '../lib/analytics';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;

    document.title = `${post.title} | AI & Web Solutions IOM`;

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const canonicalUrl = `https://aiwebsolutions.im/blog/${post.slug}`;

    setMeta('description', post.metaDescription);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    setMeta('og:title', post.title, true);
    setMeta('og:description', post.metaDescription, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:type', 'article', true);

    setMeta('twitter:card', 'summary');
    setMeta('twitter:title', post.title);
    setMeta('twitter:description', post.metaDescription);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.id = 'blog-post-schema';
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.metaDescription,
      datePublished: '2026-06-26',
      dateModified: '2026-06-26',
      author: {
        '@type': 'Organization',
        name: 'AI & Web Solutions IOM',
        url: 'https://aiwebsolutions.im',
      },
      publisher: {
        '@type': 'Organization',
        name: 'AI & Web Solutions IOM',
        url: 'https://aiwebsolutions.im',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
    });
    document.head.appendChild(schema);

    const breadcrumbSchema = document.createElement('script');
    breadcrumbSchema.type = 'application/ld+json';
    breadcrumbSchema.id = 'breadcrumb-schema';
    breadcrumbSchema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://aiwebsolutions.im/' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://aiwebsolutions.im/blog' },
        { '@type': 'ListItem', position: 3, name: post.title, item: canonicalUrl },
      ],
    });
    document.head.appendChild(breadcrumbSchema);

    return () => {
      document.title = 'Web Solutions | aiwebsolutions.im';
      const schemaEl = document.getElementById('blog-post-schema');
      if (schemaEl) schemaEl.remove();
      const breadcrumbEl = document.getElementById('breadcrumb-schema');
      if (breadcrumbEl) breadcrumbEl.remove();
      if (canonical) canonical.setAttribute('href', 'https://aiwebsolutions.im/');
    };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen pt-24 lg:pt-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16 text-center">
          <h1 className="text-2xl font-semibold text-white mb-4">Article not found</h1>
          <p className="text-gray-500 mb-8">The blog post you are looking for does not exist.</p>
          <Link to="/blog" className="btn-primary inline-flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter((p) => p.id !== post.id)
    .filter((p) => p.category === post.category)
    .slice(0, 2);

  if (relatedPosts.length < 2) {
    const extras = blogPosts
      .filter((p) => p.id !== post.id && !relatedPosts.includes(p))
      .slice(0, 2 - relatedPosts.length);
    relatedPosts.push(...extras);
  }

  const handleWhatsApp = () => {
    trackButtonClick('Message Us CTA', 'Blog Post');
    window.open('https://wa.me/4407624474095', '_blank');
  };

  return (
    <div className="min-h-screen pt-24 lg:pt-28">
      <article className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16">
        <nav className="flex items-center text-sm text-gray-500 mb-8 flex-wrap gap-1">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="h-3.5 w-3.5 mx-1 opacity-70" />
          <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
          <ChevronRight className="h-3.5 w-3.5 mx-1 opacity-70" />
          <span className="text-gray-400 line-clamp-1">{post.title}</span>
        </nav>

        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-5">
          <span className="tag-pill">{post.category}</span>
          <div className="flex items-center">
            <Calendar className="h-3.5 w-3.5 mr-1.5 opacity-70" />
            <span>{post.publishedDate}</span>
          </div>
          <div className="flex items-center">
            <Clock className="h-3.5 w-3.5 mr-1.5 opacity-70" />
            <span>{post.readingTime}</span>
          </div>
        </div>

        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-8 leading-tight">
          {post.title}
        </h1>

        <div className="prose prose-invert max-w-none">
          <div className="text-gray-400 leading-relaxed space-y-4">
            {post.content.split('\n\n').map((paragraph, index) => {
              const trimmed = paragraph.trim();
              if (!trimmed) return null;
              if (trimmed.startsWith('## ')) {
                return (
                  <h2 key={index} className="text-xl font-semibold text-white mt-10 mb-4">
                    {trimmed.replace('## ', '')}
                  </h2>
                );
              }
              if (trimmed.startsWith('### ')) {
                return (
                  <h3 key={index} className="text-lg font-medium text-white mt-8 mb-3">
                    {trimmed.replace('### ', '')}
                  </h3>
                );
              }
              const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
              if (linkRegex.test(trimmed)) {
                const parts: React.ReactNode[] = [];
                let lastIndex = 0;
                linkRegex.lastIndex = 0;
                let match;
                while ((match = linkRegex.exec(trimmed)) !== null) {
                  if (match.index > lastIndex) {
                    parts.push(trimmed.slice(lastIndex, match.index));
                  }
                  const linkText = match[1];
                  const linkHref = match[2];
                  if (linkHref.startsWith('/')) {
                    parts.push(
                      <Link key={`${index}-${match.index}`} to={linkHref} className="text-primary/90 hover:text-primary underline underline-offset-2 transition-colors">
                        {linkText}
                      </Link>
                    );
                  } else {
                    parts.push(
                      <a key={`${index}-${match.index}`} href={linkHref} target="_blank" rel="noopener noreferrer" className="text-primary/90 hover:text-primary underline underline-offset-2 transition-colors">
                        {linkText}
                      </a>
                    );
                  }
                  lastIndex = match.index + match[0].length;
                }
                if (lastIndex < trimmed.length) {
                  parts.push(trimmed.slice(lastIndex));
                }
                return <p key={index} className="text-sm sm:text-base">{parts}</p>;
              }
              return <p key={index} className="text-sm sm:text-base">{trimmed}</p>;
            })}
          </div>
        </div>

        <div className="mt-12 p-6 lg:p-8 bg-primary/[0.08] rounded-xl border border-primary/15">
          <h3 className="text-white font-medium text-lg mb-2">Want this looked at for your business?</h3>
          <p className="text-gray-500 text-sm mb-5 leading-relaxed">
            If your website, Google profile or enquiry flow is not pulling its weight, send us a message and we'll take a look.
          </p>
          <button onClick={handleWhatsApp} className="btn-primary text-sm inline-flex items-center">
            <MessageCircle className="mr-2 h-4 w-4" />
            Message us
          </button>
        </div>

        {relatedPosts.length > 0 && (
          <div className="mt-16">
            <h3 className="text-lg font-medium text-white mb-6">Related articles</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              {relatedPosts.map((related) => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="card-premium p-5 group"
                >
                  <span className="tag-pill text-xs mb-3 inline-block">{related.category}</span>
                  <h4 className="text-sm font-medium text-white group-hover:text-primary/90 transition-colors line-clamp-2 mb-2">
                    {related.title}
                  </h4>
                  <span className="text-primary/80 flex items-center text-xs font-medium">
                    Read more <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-white/[0.06]">
          <Link to="/blog" className="text-sm text-gray-500 hover:text-white transition-colors inline-flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to all articles
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
